import { useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  Loader,
  Logo,
  Typography,
} from "@saviynt/design-system";
import { integrationApi } from "@repo/api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FormattedMessage, useIntl } from "react-intl";
import "./index.css";

export const Route = createFileRoute("/")({
  component: Index,
});

const LANDING_QUERY_KEY = ["integrations", "landing"] as const;
const PAGE_SIZE = 20;

export function Index() {
  const intl = useIntl();
  const navigate = useNavigate();

  const integrationsQuery = useQuery({
    queryKey: LANDING_QUERY_KEY,
    queryFn: () =>
      integrationApi.getIntegrations({
        sortField: "createdDate",
        sortDirection: "ASC",
        onlyEnabled: false,
        pageSize: PAGE_SIZE,
      }),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const goToIntegrations = () => navigate({ to: "/integrations" });

  const integrations = integrationsQuery.data?.integrations ?? [];
  const hasIntegrations = integrations.length > 0;

  useEffect(() => {
    if (integrationsQuery.isSuccess && hasIntegrations) {
      void navigate({ to: "/dashboard" });
    }
  }, [integrationsQuery.isSuccess, hasIntegrations, navigate]);

  if (integrationsQuery.isLoading) {
    return (
      <section
        className="LandingHero flex flex-col items-center justify-center min-h-[calc(100vh-80px)] gap-6 px-6 text-center"
        aria-busy="true"
      >
        <div className="LandingHero__logoMark">
          <Logo kind="ispm" />
        </div>
        <Loader kind="loop" format="main" size="medium" color="brand" />
        <Typography kind="body2" color="--color-foreground-neutral-medium">
          <FormattedMessage
            id="CyBkk9"
            description="Loading message on the ISPM landing page"
            defaultMessage="Preparing your workspace..."
          />
        </Typography>
      </section>
    );
  }

  if (integrationsQuery.isError) {
    return (
      <section className="LandingHero flex flex-col items-center justify-center min-h-[calc(100vh-80px)] gap-6 px-6 text-center">
        <Logo kind="ispm" />
        <Typography kind="h2" color="--color-foreground-neutral-bold">
          <FormattedMessage
            id="xmXgHM"
            description="Title shown when landing page failed to load integrations"
            defaultMessage="We couldn't load your workspace"
          />
        </Typography>
        <Typography kind="body2" color="--color-foreground-neutral-medium">
          <FormattedMessage
            id="OQ0Hal"
            description="Subtitle for landing page load error"
            defaultMessage="Try again, or head to integrations to continue setup."
          />
        </Typography>
        <div className="flex gap-3">
          <Button kind="filled" onClick={() => integrationsQuery.refetch()}>
            {intl.formatMessage({
              id: "fvA86S",
              defaultMessage: "Try again",
              description: "Retry button on landing page error",
            })}
          </Button>
          <Button kind="outlined" onClick={goToIntegrations}>
            {intl.formatMessage({
              id: "An48bt",
              defaultMessage: "Go to integrations",
              description: "Fallback link on landing page error",
            })}
          </Button>
        </div>
      </section>
    );
  }

  if (!hasIntegrations) {
    return (
      <main className="LandingHero LandingHero--empty">
        <section className="LandingHero__ready flex flex-col gap-12 max-w-[1120px] mx-auto">
          <header className="LandingHero__hero">
            <div
              className="absolute inset-0 pointer-events-none overflow-hidden"
              aria-hidden="true"
            >
              <span className="absolute rounded-full blur-[60px] opacity-65 LandingHero__blob--a" />
              <span className="absolute rounded-full blur-[60px] opacity-65 LandingHero__blob--b" />
              <span className="LandingHero__heroGrid" />
            </div>
            <div className="LandingHero__heroInner relative z-[1] flex items-center gap-12">
              <div className="LandingHero__logoMark LandingHero__logoMark--md p-4 w-24 h-24 shrink-0">
                <Logo kind="ispm" />
              </div>
              <div className="flex flex-col gap-3 flex-1 min-w-0">
                <span className="LandingHero__eyebrow LandingHero__eyebrow--neutral inline-flex items-center gap-2 py-1 px-3 rounded-full self-start uppercase tracking-[0.08em]">
                  <span
                    className="LandingHero__eyebrowDot LandingHero__eyebrowDot--idle"
                    aria-hidden="true"
                  />
                  <Typography
                    kind="label"
                    color="--color-foreground-neutral-medium"
                  >
                    <FormattedMessage
                      id="cGqyR6"
                      description="Eyebrow tag for empty-state landing page"
                      defaultMessage="Get started"
                    />
                  </Typography>
                </span>
                <Typography
                  kind="display3-bold"
                  color="--color-foreground-neutral-bold"
                  className="LandingHero__heroTitle"
                >
                  <FormattedMessage
                    id="VdQzYZ"
                    description="Welcome message on home page"
                    defaultMessage="Welcome to ISPM"
                  />
                </Typography>
                <Typography
                  kind="body1"
                  color="--color-foreground-neutral-medium"
                  className="max-w-[56ch]"
                >
                  <FormattedMessage
                    id="vLzfER"
                    description="Lead paragraph for first-time users on landing page"
                    defaultMessage="Connect your first source to start ingesting identity, access, and agent data — your dashboard activates automatically once data flows in."
                  />
                </Typography>
                <div className="flex flex-wrap gap-3 mt-3">
                  <Button kind="filled" size="large" onClick={goToIntegrations}>
                    {intl.formatMessage({
                      id: "YpDbpB",
                      defaultMessage: "Add your first integration",
                      description: "Primary CTA for empty landing page",
                    })}
                  </Button>
                </div>
              </div>
            </div>
          </header>

          <div className="LandingHero__steps">
            <article className="LandingHero__step LandingHero__step--active relative flex items-start gap-[14px] p-5 rounded-xl">
              <span
                className="LandingHero__stepIndex inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm shrink-0"
                aria-hidden="true"
              >
                <FormattedMessage
                  id="OHcwy5"
                  description="Step 1 numeric indicator"
                  defaultMessage="1"
                />
              </span>
              <div className="flex flex-col gap-1 min-w-0">
                <Typography
                  kind="body2-bold"
                  color="--color-foreground-neutral-bold"
                >
                  <FormattedMessage
                    id="q8/ydB"
                    description="Onboarding step 1 title"
                    defaultMessage="Connect a source"
                  />
                </Typography>
                <Typography
                  kind="body3"
                  color="--color-foreground-neutral-medium"
                >
                  <FormattedMessage
                    id="drer4l"
                    description="Onboarding step 1 description"
                    defaultMessage="Add credentials for AWS, Azure, GCP, or any supported app."
                  />
                </Typography>
              </div>
            </article>

            <span
              className="LandingHero__stepConnector self-center h-[2px] w-full rounded-full opacity-70"
              aria-hidden="true"
            />

            <article className="LandingHero__step relative flex items-start gap-[14px] p-5 rounded-xl">
              <span
                className="LandingHero__stepIndex inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm shrink-0"
                aria-hidden="true"
              >
                <FormattedMessage
                  id="ArwUqy"
                  description="Step 2 numeric indicator"
                  defaultMessage="2"
                />
              </span>
              <div className="flex flex-col gap-1 min-w-0">
                <Typography
                  kind="body2-bold"
                  color="--color-foreground-neutral-bold"
                >
                  <FormattedMessage
                    id="YdN4t8"
                    description="Onboarding step 2 title"
                    defaultMessage="Ingest your data"
                  />
                </Typography>
                <Typography
                  kind="body3"
                  color="--color-foreground-neutral-medium"
                >
                  <FormattedMessage
                    id="OcCARK"
                    description="Onboarding step 2 description"
                    defaultMessage="We'll discover identities, accounts, and access automatically."
                  />
                </Typography>
              </div>
            </article>

            <span
              className="LandingHero__stepConnector self-center h-[2px] w-full rounded-full opacity-70"
              aria-hidden="true"
            />

            <article className="LandingHero__step relative flex items-start gap-[14px] p-5 rounded-xl">
              <span
                className="LandingHero__stepIndex inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm shrink-0"
                aria-hidden="true"
              >
                <FormattedMessage
                  id="RW+OVO"
                  description="Step 3 numeric indicator"
                  defaultMessage="3"
                />
              </span>
              <div className="flex flex-col gap-1 min-w-0">
                <Typography
                  kind="body2-bold"
                  color="--color-foreground-neutral-bold"
                >
                  <FormattedMessage
                    id="cI51Be"
                    description="Onboarding step 3 title"
                    defaultMessage="Unlock your dashboard"
                  />
                </Typography>
                <Typography
                  kind="body3"
                  color="--color-foreground-neutral-medium"
                >
                  <FormattedMessage
                    id="QPaEyb"
                    description="Onboarding step 3 description"
                    defaultMessage="Insights, posture, and actions activate the moment data flows in."
                  />
                </Typography>
              </div>
            </article>
          </div>

          <div className="LandingHero__valueGrid grid grid-cols-3 gap-6 w-full max-w-[960px] mb-6">
            <Card className="LandingHero__valueCard">
              <CardBody className="flex flex-col gap-3 !p-6 text-left">
                <span
                  className="LandingHero__valueGlyph--info inline-flex items-center justify-center w-10 h-10 rounded-lg text-[22px] leading-none"
                  aria-hidden="true"
                />
                <Typography
                  kind="h4"
                  color="--color-foreground-neutral-bold"
                  className="mb-1"
                >
                  <FormattedMessage
                    id="UQZkkg"
                    description="Discover value prop title"
                    defaultMessage="Discover"
                  />
                </Typography>
                <Typography
                  kind="body2"
                  color="--color-foreground-neutral-medium"
                >
                  <FormattedMessage
                    id="EYLR4u"
                    description="Discover value prop body"
                    defaultMessage="Map every identity, account, and access path across your environment."
                  />
                </Typography>
              </CardBody>
            </Card>

            <Card className="LandingHero__valueCard">
              <CardBody className="flex flex-col gap-3 !p-6 text-left">
                <span
                  className="LandingHero__valueGlyph--success inline-flex items-center justify-center w-10 h-10 rounded-lg text-[22px] leading-none"
                  aria-hidden="true"
                />
                <Typography
                  kind="h4"
                  color="--color-foreground-neutral-bold"
                  className="mb-1"
                >
                  <FormattedMessage
                    id="bnDrUp"
                    description="Govern value prop title"
                    defaultMessage="Govern"
                  />
                </Typography>
                <Typography
                  kind="body2"
                  color="--color-foreground-neutral-medium"
                >
                  <FormattedMessage
                    id="piTjqy"
                    description="Govern value prop body"
                    defaultMessage="Enforce least privilege and policy across clouds, apps, and AI agents."
                  />
                </Typography>
              </CardBody>
            </Card>

            <Card className="LandingHero__valueCard">
              <CardBody className="flex flex-col gap-3 !p-6 text-left">
                <span
                  className="LandingHero__valueGlyph--primary inline-flex items-center justify-center w-10 h-10 rounded-lg text-[22px] leading-none"
                  aria-hidden="true"
                />
                <Typography
                  kind="h4"
                  color="--color-foreground-neutral-bold"
                  className="mb-1"
                >
                  <FormattedMessage
                    id="mDGXS5"
                    description="Act value prop title"
                    defaultMessage="Act"
                  />
                </Typography>
                <Typography
                  kind="body2"
                  color="--color-foreground-neutral-medium"
                >
                  <FormattedMessage
                    id="H9aIjC"
                    description="Act value prop body"
                    defaultMessage="Auto-remediate risky access in real time with intelligent recommendations."
                  />
                </Typography>
              </CardBody>
            </Card>
          </div>
        </section>
      </main>
    );
  }

  // Integrations exist — redirect to dashboard is in flight via useEffect above
  return null;
}
